'use client'

/**
 * Automerge Provider Component
 *
 * This client component wraps the app with RepoContext.Provider.
 * Must be a client component because Automerge repo uses browser APIs (IndexedDB).
 *
 * Initialization flow:
 * 1. Sync document URL from user profile (cross-device discovery)
 * 2. Initialize Automerge repo
 * 3. Verify document is accessible (with timeout)
 * 4. Render children with repo context
 *
 * IMPORTANT: This uses peer-to-peer sync. For cross-device sync to work,
 * at least one other device with the document must be online.
 */

import { useState, useEffect, useCallback, useRef } from 'react'
import { RepoContext } from '@automerge/automerge-repo-react-hooks'
import type { Repo, AutomergeUrl } from '@automerge/automerge-repo'
import { getRepo } from '../repo/repo'
import { useDocumentUrlSync } from './use-document-url-sync'
import { getEnvironmentStorageKey, getCurrentOrigin } from '@/utils/environment'
import { decideNewUserAction } from '../repo/new-user-detection'

type AutomergeProviderProps = {
  children: React.ReactNode
}

type SyncState =
  | 'loading'
  | 'syncing'
  | 'ready'
  | 'error'
  | 'waiting-for-peer'
  | 'no-data'

type SyncErrorType = 'timeout' | 'network' | 'unknown'

export function AutomergeProvider({ children }: AutomergeProviderProps) {
  const [repo, setRepo] = useState<Repo | null>(null)
  const [syncState, setSyncState] = useState<SyncState>('loading')
  const [syncErrorType, setSyncErrorType] = useState<SyncErrorType>('unknown')
  const [retryCount, setRetryCount] = useState(0)
  const [syncProgress, setSyncProgress] = useState(0)
  const [documentUrlFromProfile, setDocumentUrlFromProfile] = useState<
    string | null
  >(null)
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Sync document URL from profile before initializing repo
  const {
    ready: urlSyncReady,
    documentUrl,
    profileChecked,
    isAuthenticated,
  } = useDocumentUrlSync()
  // Ref to avoid adding profileChecked to effect deps (it's set in the same render as urlSyncReady)
  const profileCheckedRef = useRef(false)
  profileCheckedRef.current = profileChecked

  // Clear document URL and start fresh
  const handleStartFresh = useCallback(async () => {
    const confirmed = window.confirm(
      'This will create a new empty document. Your data on other devices will NOT be deleted, but this device will start fresh.\n\nContinue?',
    )

    if (!confirmed) return

    // Clear localStorage
    const storageKey = getEnvironmentStorageKey('automerge-doc-url')
    localStorage.removeItem(storageKey)

    // Clear profile document URL for this environment
    try {
      const origin = getCurrentOrigin()
      const { saveAutomergeDocumentUrl } = await import('./actions')
      await saveAutomergeDocumentUrl('', origin)
    } catch (error) {
      console.warn('Failed to clear profile document URL:', error)
    }

    // Reload the page to reinitialize
    window.location.reload()
  }, [])

  // Verify document is accessible with timeout
  const verifyDocumentAccess = useCallback(
    async (repoInstance: Repo, docUrl: string | null) => {
      if (!docUrl) {
        // No document URL - new user, will create document on first use
        console.log('[Sync] No existing document URL - new user')
        setSyncState('ready')
        return true
      }

      setDocumentUrlFromProfile(docUrl)

      // Try local-first: check if doc is already cached in IndexedDB
      try {
        const handle = await repoInstance.find(docUrl as AutomergeUrl)
        const localDoc = handle.doc() as
          | { entries?: Record<string, unknown> }
          | undefined

        if (localDoc && localDoc.entries !== undefined) {
          console.log(
            '[Sync] Local data available:',
            Object.keys(localDoc.entries).length,
            'entries',
          )
          setSyncState('ready')
          return true
        }
      } catch {
        // No local data — fall through to remote sync
      }

      // No local data — show syncing UI and wait for remote peer
      setSyncState('syncing')
      setSyncProgress(0)

      const SYNC_TIMEOUT_MS = 30000

      const progressInterval = setInterval(() => {
        setSyncProgress((prev) => Math.min(prev + 10, 90))
      }, SYNC_TIMEOUT_MS / 10)

      try {
        console.log(`[Sync] Waiting for remote peer: ${docUrl.slice(0, 30)}...`)

        const handle = await repoInstance.find(docUrl as AutomergeUrl)

        await Promise.race([
          handle.whenReady(),
          new Promise<never>((_, reject) => {
            syncTimeoutRef.current = setTimeout(() => {
              reject(new Error('Document sync timeout'))
            }, SYNC_TIMEOUT_MS)
          }),
        ])

        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current)
          syncTimeoutRef.current = null
        }

        clearInterval(progressInterval)
        setSyncProgress(100)

        const doc = handle.doc() as
          | { entries?: Record<string, unknown> }
          | undefined
        if (doc && doc.entries !== undefined) {
          console.log(
            '[Sync] Document synced:',
            Object.keys(doc.entries).length,
            'entries',
          )
          setSyncState('ready')
          return true
        }

        console.log('[Sync] Document synced (empty)')
        setSyncState('ready')
        return true
      } catch (error) {
        clearInterval(progressInterval)

        if (syncTimeoutRef.current) {
          clearTimeout(syncTimeoutRef.current)
          syncTimeoutRef.current = null
        }

        console.warn('[Sync] Failed:', error)

        const errorMessage =
          error instanceof Error ? error.message : String(error)
        if (errorMessage.includes('timeout')) {
          setSyncErrorType('timeout')
          setSyncState('waiting-for-peer')
        } else if (
          errorMessage.includes('network') ||
          errorMessage.includes('fetch')
        ) {
          setSyncErrorType('network')
          setSyncState('error')
        } else {
          setSyncErrorType('unknown')
          setSyncState('waiting-for-peer')
        }

        return false
      }
    },
    [],
  )

  useEffect(() => {
    // Wait for document URL sync to complete before creating repo
    if (!urlSyncReady) return
    // Don't initialize Automerge for unauthenticated users
    if (!isAuthenticated) return

    let cancelled = false

    async function initRepo() {
      try {
        const repoInstance = await getRepo()
        if (cancelled) return

        setRepo(repoInstance)

        // Get document URL from localStorage
        const storageKey = getEnvironmentStorageKey('automerge-doc-url')
        let savedDocUrl = localStorage.getItem(storageKey)

        // No localStorage URL — either new user or cleared storage
        const decision = decideNewUserAction(
          savedDocUrl,
          profileCheckedRef.current,
        )

        if (decision?.action === 'show-error') {
          console.warn('[Sync]', decision.reason)
          if (!cancelled) {
            setSyncErrorType('network')
            setSyncState('error')
          }
          return
        }

        if (decision?.action === 'create-document') {
          console.log(
            '[Sync] New user (verified via profile) — creating document',
          )
          const handle =
            repoInstance.create<Record<string, Record<string, unknown>>>()
          handle.change((doc) => {
            doc.entries = {}
            doc.topics = {}
            doc.areas = {}
            doc.projects = {}
            doc.links = {}
          })
          await handle.whenReady()
          savedDocUrl = handle.url
          localStorage.setItem(storageKey, savedDocUrl)
          localStorage.setItem('storage-backend', 'automerge')

          // Save to profile for cross-device sync (non-blocking)
          const origin = getCurrentOrigin()
          import('./actions')
            .then(({ saveAutomergeDocumentUrl }) =>
              saveAutomergeDocumentUrl(savedDocUrl!, origin),
            )
            .catch((err) =>
              console.warn(
                '[Sync] Failed to save new document URL to profile:',
                err,
              ),
            )
        }

        await verifyDocumentAccess(repoInstance, savedDocUrl)
      } catch (error) {
        // Log comprehensive error details
        console.error('Failed to initialize Automerge repo:', {
          error,
          message: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          urlSyncReady,
          retryCount,
        })
        if (!cancelled) {
          setSyncState('error')
        }
      }
    }

    initRepo()

    return () => {
      cancelled = true
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current)
      }
    }
  }, [urlSyncReady, isAuthenticated, retryCount, verifyDocumentAccess])

  const handleRetry = useCallback(() => {
    setSyncState('loading')
    setSyncProgress(0)
    setRetryCount((c) => c + 1)
  }, [])

  // Not authenticated — render children without Automerge (landing page, auth pages)
  if (!isAuthenticated) {
    return <>{children}</>
  }

  // Show "waiting for peer" UI - specific to peer-to-peer sync
  if (syncState === 'waiting-for-peer') {
    return (
      <div className="bg-background fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <h1 className="mb-2 text-xl font-semibold">
            Waiting for your other device
          </h1>
          <p className="text-muted-foreground mb-4 text-sm">
            Your data is stored on your other device. To sync it here:
          </p>

          <div className="mb-6 space-y-3 text-left">
            <div className="border-border bg-muted/30 flex items-start gap-3 rounded-lg border p-3">
              <span className="text-muted-foreground text-sm font-medium">
                1
              </span>
              <div>
                <div className="text-sm font-medium">Open Contour</div>
                <div className="text-muted-foreground text-xs">
                  On the device where you first set up the app
                </div>
              </div>
            </div>
            <div className="border-border bg-muted/30 flex items-start gap-3 rounded-lg border p-3">
              <span className="text-muted-foreground text-sm font-medium">
                2
              </span>
              <div>
                <div className="text-sm font-medium">Keep it open</div>
                <div className="text-muted-foreground text-xs">
                  The app needs to be active to sync your data
                </div>
              </div>
            </div>
            <div className="border-border bg-muted/30 flex items-start gap-3 rounded-lg border p-3">
              <span className="text-muted-foreground text-sm font-medium">
                3
              </span>
              <div>
                <div className="text-sm font-medium">Try again here</div>
                <div className="text-muted-foreground text-xs">
                  Once your other device is open, click retry below
                </div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-3">
            <button
              onClick={handleRetry}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-md px-6 py-2.5 text-sm font-medium transition-colors"
            >
              Try Again
            </button>

            <button
              onClick={handleStartFresh}
              className="border-border bg-background hover:bg-muted w-full rounded-md border px-6 py-2.5 text-sm font-medium transition-colors"
            >
              Start Fresh Instead
            </button>
          </div>

          <p className="text-muted-foreground mt-4 text-xs">
            Document ID: {documentUrlFromProfile?.slice(10, 30)}...
          </p>

          <div className="border-border mt-4 rounded-lg border p-3 text-left">
            <div className="text-muted-foreground text-xs font-medium">
              Why is this needed?
            </div>
            <div className="text-muted-foreground mt-1 text-xs">
              Contour uses peer-to-peer sync for privacy. Your data lives on
              your devices, not our servers. This means at least one device with
              your data needs to be online to sync.
            </div>
          </div>
        </div>
      </div>
    )
  }

  // Show sync error UI with retry option
  if (syncState === 'error') {
    return (
      <div className="bg-background fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="max-w-md text-center">
          <h1 className="mb-2 text-xl font-semibold">Connection Error</h1>
          <p className="text-muted-foreground mb-6 text-sm">
            {syncErrorType === 'network'
              ? 'Unable to connect to the sync service. Please check your internet connection.'
              : 'Something went wrong while trying to sync your data. Please try again.'}
          </p>
          <div className="flex flex-col gap-3">
            <button
              onClick={handleRetry}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full rounded-md px-6 py-2.5 text-sm font-medium transition-colors"
            >
              Try Again
            </button>

            <button
              onClick={handleStartFresh}
              className="border-border bg-background hover:bg-muted w-full rounded-md border px-6 py-2.5 text-sm font-medium transition-colors"
            >
              Start Fresh Instead
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Show syncing state with progress
  if (syncState === 'syncing') {
    return (
      <div className="bg-background fixed inset-0 z-50 flex items-center justify-center">
        <div className="w-full max-w-xs text-center">
          <div className="text-lg font-medium">Syncing your data</div>
          <div className="text-muted-foreground mt-2 text-sm">
            Looking for your other devices
          </div>

          {/* Progress bar */}
          <div className="bg-muted mt-6 h-2 w-full overflow-hidden rounded-full">
            <div
              className="bg-primary h-full transition-all duration-300"
              style={{ width: `${syncProgress}%` }}
            />
          </div>

          <div className="text-muted-foreground mt-2 text-xs">
            This may take up to 30 seconds...
          </div>
        </div>
      </div>
    )
  }

  // Don't render children until repo is ready
  if (!repo || syncState === 'loading') {
    return null
  }

  return <RepoContext.Provider value={repo}>{children}</RepoContext.Provider>
}
